/**
 * lib/applicationFormPdf.ts
 * ------------------------------------------------------------------
 * Faithful PDF recreation of the paper "LKC Student Admission
 * Application Form", pre-filled from a (now-complete) ApplicationRecord.
 *
 * Purpose: the admissions office can print a filled application that
 * looks like the school's own paper form — captured fields pre-filled,
 * not-captured fields shown as blank ruled lines ready for pen, and the
 * office sign-off / checklist / conditions areas reproduced verbatim as
 * blank reference sections.
 *
 * Style: the house chrome (drawLetterhead / stampFooters / pageGeom / PDF
 * palette) plus the same BANDED AUTOTABLE language as the marksheet and the
 * student/department analysis sheets — navy section bands, navy field
 * labels, tinted alternate rows, hairline rules. Asked for in Aug 2026 so a
 * printed application sits in the same folder as everything else the school
 * prints and looks like it came from the same place.
 *
 * The captured details are tables; the parts that are genuinely for pen —
 * the checklist tick boxes, the declaration, the signature and stamp blocks
 * in §8 — stay as ruled blank lines, because a banded table with nothing in
 * it invites nobody to write on it.
 *
 * `row()` BUFFERS rather than drawing. Anything that is not a field row
 * (a section band, a subhead, a paragraph, a signature rule) flushes the
 * buffer first, so the reading order is exactly the call order and the
 * sixty-odd row() call sites did not have to change. autoTable handles its
 * own pagination and redraws the letterhead per page.
 *
 *   buildApplicationFormPdf(app, { logoDataUrl }).save('Application_….pdf')
 *
 * NOTE: the §7 fee statement, document lists and Conditions of Admission
 * are EDITABLE config — passed in via opts.formText (from Firestore
 * `reportSchoolSettings/admissionFormText`), falling back to the seeded
 * defaults in lib/admissionFormDefaults when absent. Admins edit them in
 * Admissions → Settings; no code change needed to correct the wording.
 * ------------------------------------------------------------------
 */
import { jsPDF } from 'jspdf';
import autoTable, { type RowInput } from 'jspdf-autotable';
import { SCHOOL } from '@/lib/school.config';
import {
  drawLetterhead,
  stampFooters,
  pageGeom,
  CONTENT_TOP,
  PDF,
} from '@/lib/pdfChrome';
import type {
  ApplicationRecord,
  Guardian,
  PreviousSchoolEntry,
  AdmissionFormText,
} from '@/types';
import { overallPercent as entranceOverallPercent } from '@/lib/entranceAssessment';
import { resolveAdmissionFormText } from '@/lib/admissionFormDefaults';

export interface ApplicationFormPdfOptions {
  logoDataUrl?: string | null;
  /** Editable §7 boilerplate (fee statement, doc lists, conditions).
   *  Missing/empty parts fall back to lib/admissionFormDefaults. */
  formText?: Partial<AdmissionFormText> | null;
}

const ROLE_LABEL: Record<string, string> = {
  father: 'Father',
  mother: 'Mother',
  guardian: 'Guardian',
  stepfather: 'Stepfather',
  stepmother: 'Stepmother',
  grandparent: 'Grandparent',
  other: 'Other',
};
const roleLabel = (g: Guardian) =>
  g.role === 'other' ? g.roleLabel || 'Other' : ROLE_LABEL[g.role] ?? 'Guardian';

export function buildApplicationFormPdf(
  app: ApplicationRecord,
  opts: ApplicationFormPdfOptions = {},
): jsPDF {
  const formText = resolveAdmissionFormText(opts.formText);
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const geom = pageGeom('p');
  const { MX, right, PH } = geom;
  const contentW = right - MX;
  const BOTTOM = PH - 14;
  const TITLE = 'APPLICATION FOR ADMISSION';
  const metaLine = `Ref ${app.referenceNumber || '—'}`;

  const setT = (c: readonly number[]) => pdf.setTextColor(c[0], c[1], c[2]);
  const setD = (c: readonly number[]) => pdf.setDrawColor(c[0], c[1], c[2]);
  const setF = (c: readonly number[]) => pdf.setFillColor(c[0], c[1], c[2]);
  const v = (s?: string | null) => (s ?? '').toString().trim();
  const yn = (b?: boolean) => (b === true ? 'YES' : b === false ? 'NO' : '');

  let y = CONTENT_TOP + 3;
  drawLetterhead(pdf, { title: TITLE, metaLine, logoDataUrl: opts.logoDataUrl, geom });

  /** Add a page + redraw the letterhead if `h` mm won't fit. */
  function ensure(h: number) {
    if (y + h > BOTTOM) {
      pdf.addPage();
      drawLetterhead(pdf, { title: TITLE, metaLine, logoDataUrl: opts.logoDataUrl, geom });
      y = CONTENT_TOP + 3;
    }
  }

  /** Navy section band — the same band the analysis sheets head a table with. */
  function section(title: string) {
    flushRows();
    ensure(16);
    setF(PDF.NAVY);
    pdf.rect(MX, y, contentW, 6.4, 'F');
    setT(PDF.WHITE);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text(title, MX + 2, y + 4.4);
    y += 9;
  }

  /* ---- field rows, buffered and flushed as a banded table ----
     Buffered so that consecutive row() calls become ONE table: a table per
     row would band nothing and rule everything twice. */
  let pending: RowInput[] = [];

  const labelCell = (t: string) => ({
    content: t.toUpperCase(),
    styles: {
      textColor: PDF.NAVY,
      fontStyle: 'bold' as const,
      fontSize: 6.6,
      cellWidth: contentW * 0.26,
    },
  });

  /** One row: a label/value pair, or two side by side. */
  function row(a: [string, string], b?: [string, string]) {
    if (b) {
      pending.push([labelCell(a[0]), a[1], labelCell(b[0]), b[1]]);
    } else {
      // A wide value spans the three remaining columns so long answers —
      // an address, a medical note — get the full width rather than wrapping
      // inside a quarter of the page.
      pending.push([labelCell(a[0]), { content: a[1], colSpan: 3 }]);
    }
  }

  /** Draw whatever rows have accumulated, then reset. */
  function flushRows() {
    if (pending.length === 0) return;
    autoTable(pdf, {
      body: pending,
      startY: y,
      margin: { top: CONTENT_TOP + 3, left: MX, right: MX, bottom: 14 },
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 8.5,
        textColor: PDF.INK,
        cellPadding: { top: 1.8, right: 2, bottom: 1.8, left: 2 },
        lineColor: PDF.RULE,
        lineWidth: { bottom: 0.1, top: 0, left: 0, right: 0 },
        valign: 'middle',
        overflow: 'linebreak',
      },
      // The tint is what makes an empty field legible as a field. Without it
      // a blank row on a blank page is invisible and nobody fills it in.
      alternateRowStyles: { fillColor: PDF.BAND },
      didDrawPage: () => {
        drawLetterhead(pdf, { title: TITLE, metaLine, logoDataUrl: opts.logoDataUrl, geom });
      },
    });
    const t = (pdf as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable;
    y = (t ? t.finalY : y) + 3;
    pending = [];
  }

  /** A small bold sub-heading inside a section. */
  function subhead(text: string) {
    flushRows();
    ensure(9);
    setT(PDF.NAVY);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8.5);
    pdf.text(text, MX + 1, y + 3);
    y += 6.5;
  }

  /** Wrapped paragraph text. */
  function para(text: string, size = 8, indent = 0) {
    flushRows();
    setT(PDF.INK);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(text, contentW - indent) as string[];
    for (const ln of lines) {
      ensure(size * 0.55 + 1.5);
      pdf.text(ln, MX + indent, y);
      y += size * 0.5 + 1.4;
    }
  }

  // ============================================================
  // Intro block (below the letterhead): address, photo box, note
  // ============================================================
  ensure(26);
  // photo box on the right
  const photoW = 34;
  const photoH = 22;
  setD(PDF.RULE);
  pdf.setLineWidth(0.3);
  pdf.rect(right - photoW, y, photoW, photoH);
  setT(PDF.MUTED);
  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(6.6);
  pdf.text(
    pdf.splitTextToSize('Affix two (2) recent passport photographs', photoW - 4) as string[],
    right - photoW + 2,
    y + 6,
  );
  // address block on the left
  setT(PDF.INK);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.text('Admissions Office', MX, y + 3);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.6);
  setT(PDF.MUTED);
  let ay = y + 7;
  for (const ln of SCHOOL.contact.addressLines) {
    pdf.text(ln, MX, ay);
    ay += 3.6;
  }
  pdf.text(
    `Tel ${SCHOOL.contact.phonePrimary} / ${SCHOOL.contact.phoneSecondary}`,
    MX,
    ay,
  );
  ay += 3.6;
  pdf.text(`${SCHOOL.contact.website}`, MX, ay);
  y += photoH + 3;

  setT(PDF.NAVY);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7.6);
  pdf.text('Please complete in BLOCK LETTERS. Fields left blank should be completed by hand.', MX, y);
  y += 3;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7);
  setT(PDF.MUTED);
  pdf.text(
    `Reference ${v(app.referenceNumber) || '—'}   ·   Submitted ${v(app.submittedAt).slice(0, 10) || '—'}`,
    MX,
    y + 3.4,
  );
  y += 8;

  // ============================================================
  // §1 STUDENT DETAILS
  // ============================================================
  section('1.  STUDENT DETAILS');
  row(
    ['Level applied for', v(app.applyingForForm) ? `Form ${v(app.applyingForForm)}` : ''],
    ['Year to start', v(app.applyingForYear)],
  );
  row(['Surname', v(app.surname)], ['Name(s)', v(app.firstName)]);
  row(
    ['Date of birth', v(app.dateOfBirth)],
    ['Gender', app.gender === 'M' ? 'Male' : app.gender === 'F' ? 'Female' : ''],
  );
  row(['Nationality', v(app.nationality)], ['Place of birth', v(app.placeOfBirth)]);
  row(['Native language', v(app.homeLanguage)], ['Religion', v(app.religion)]);
  row(['ID / passport number', v(app.idPassportNumber)], ['Email / phone', [v(app.studentEmail), v(app.studentPhone)].filter(Boolean).join('  /  ')]);
  row(['Residential address', v(app.address)]);
  row([
    'Boarding status',
    app.boardingStatus === 'boarding' ? 'Boarder' : app.boardingStatus === 'day' ? 'Day scholar' : '',
  ]);

  // ============================================================
  // §2 PARENT(S) DETAILS
  // ============================================================
  const guardians = app.guardians ?? [];
  const father = guardians.find((g) => g.role === 'father');
  const mother = guardians.find((g) => g.role === 'mother');
  const guardianExtra = guardians.find((g) => !['father', 'mother'].includes(g.role));
  const payers = guardians
    .filter((g) => g.responsibleForFees)
    .map((g) => `${roleLabel(g)}${v(g.fullName) ? ` (${v(g.fullName)})` : ''}`);

  section('2.  PARENT(S) DETAILS');
  row(['Who is responsible for paying school fees?', payers.join(',  ')]);

  function parentBlock(title: string, g?: Guardian) {
    subhead(title);
    row(['Surname', ''], ['Name(s)', v(g?.fullName)]);
    row(['Nationality', v(g?.nationality)], ['ID / passport number', v(g?.idNumber)]);
    row(['Occupation & position', v(g?.occupation)], ['Employer', v(g?.employer)]);
    row(['Primary mobile', v(g?.phone)], ['Work phone', v(g?.workPhone)]);
    row(['Home tel', v(g?.homeTel)], ['Primary email', v(g?.email)]);
    row(
      ['Emergency contact name', v(g?.emergencyContactName)],
      ['Emergency contact phone', v(g?.emergencyContactPhone)],
    );
  }
  parentBlock('FATHER', father);
  parentBlock('MOTHER', mother);

  subhead('EMERGENCY CONTACT (other than parents)');
  row(['Name', v(app.emergencyContactName)], ['Mobile', v(app.emergencyContactPhone)]);
  row(['Email', v(app.emergencyContactEmail)]);

  // ============================================================
  // §3 GUARDIAN DETAILS
  // ============================================================
  section('3.  GUARDIAN DETAILS (if not a parent above)');
  row([
    'Is the guardian responsible for fees?',
    guardianExtra ? yn(!!guardianExtra.responsibleForFees) : '',
  ]);
  row(['Surname', ''], ['Name(s)', v(guardianExtra?.fullName)]);
  row(['Nationality', v(guardianExtra?.nationality)], ['ID / passport number', v(guardianExtra?.idNumber)]);
  row(['Residential address', v(guardianExtra?.address)]);
  row(['Work / home contact', [v(guardianExtra?.workPhone), v(guardianExtra?.homeTel)].filter(Boolean).join('  /  ')], ['Primary mobile', v(guardianExtra?.phone)]);
  row(['Primary email', v(guardianExtra?.email)], ['Relationship', guardianExtra ? roleLabel(guardianExtra) : '']);
  row(
    ['Emergency contact name', v(guardianExtra?.emergencyContactName)],
    ['Emergency contact phone', v(guardianExtra?.emergencyContactPhone)],
  );

  // ============================================================
  // §4 PREVIOUS SCHOOL HISTORY
  // ============================================================
  section('4.  PREVIOUS SCHOOL HISTORY');
  const ps = app.previousSchools;
  const last: PreviousSchoolEntry =
    ps?.last && (ps.last.name || ps.last.year || ps.last.formClass || ps.last.contact)
      ? ps.last
      : { name: v(app.previousSchool) };
  const others = ps?.others ?? [];

  subhead('MOST RECENT (LAST) SCHOOL');
  row(['School name', v(last.name)], ['Year', v(last.year)]);
  row(['Form / class', v(last.formClass)], ['Contact', v(last.contact)]);

  subhead('OTHER SCHOOLS ATTENDED');
  for (let i = 0; i < 3; i++) {
    const o = others[i];
    row(['School name', v(o?.name)], ['Year', v(o?.year)]);
    row(['Form / class', v(o?.formClass)], ['Contact', v(o?.contact)]);
  }

  // ============================================================
  // §5 MEDICAL
  // ============================================================
  const med = app.medical;
  section('5.  MEDICAL INFORMATION');
  row(['Does the pupil have a medical condition?', med ? yn(!!med.hasMedicalCondition) : '']);
  row(['If yes, please give details', v(med?.medicalConditionDetails) || [v(med?.chronicConditions), v(med?.allergies)].filter(Boolean).join('; ')]);
  row([
    'Consent to emergency medical treatment / transport?',
    med ? yn(med.emergencyTreatmentConsent !== false) : '',
  ]);
  row(['If no, action the school should take', v(med?.emergencyTreatmentNoConsentAction)]);
  row(['Doctor’s name', v(med?.doctorName)], ['Doctor’s contact', v(med?.doctorPhone)]);
  row(['Medical aid name', v(med?.medicalAidProvider)], ['Medical aid number', v(med?.medicalAidNumber)]);

  // ============================================================
  // §6 ADDITIONAL INFORMATION (siblings)
  // ============================================================
  const sib = app.siblings;
  section('6.  ADDITIONAL INFORMATION');
  row(['Does another child from this family attend the College?', sib ? yn(!!sib.hasSiblingAtSchool) : '']);
  row(
    ['Sibling 1 — name(s)', v(sib?.sibling1?.names)],
    ['Sibling 1 — surname', v(sib?.sibling1?.surname)],
  );
  row(
    ['Sibling 2 — name(s)', v(sib?.sibling2?.names)],
    ['Sibling 2 — surname', v(sib?.sibling2?.surname)],
  );
  row(['Parent / guardian name(s)', v(sib?.siblingParentNames)], ['Email', v(sib?.siblingParentEmail)]);
  row(['Additional notes', v(app.notes)]);

  // ============================================================
  // §7 CHECK LIST (reference — office use)
  // ============================================================
  section('7.  CHECK LIST');
  subhead('FEES');
  para(formText.feeStatement);
  y += 1;

  subhead('DOCUMENTS REQUIRED');
  formText.documentsRequired.forEach((d, i) => {
    ensure(5);
    setT(PDF.INK);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.text(`${i + 1}.`, MX + 1, y + 3);
    pdf.text(pdf.splitTextToSize(d, contentW - 8) as string[], MX + 6, y + 3);
    y += 5;
  });
  y += 2;

  subhead('DOCUMENTS RECEIVED (office use)');
  formText.documentsReceived.forEach((d) => {
    ensure(5.5);
    setD(PDF.RULE);
    pdf.setLineWidth(0.3);
    pdf.rect(MX + 1, y, 3.4, 3.4); // blank tick box
    setT(PDF.INK);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.text(d, MX + 7, y + 3);
    y += 5.5;
  });
  y += 2;

  subhead('CONDITIONS OF ADMISSION');
  formText.conditions.forEach((c, i) => {
    ensure(6);
    setT(PDF.INK);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.6);
    pdf.text(`${i + 1}.`, MX + 1, y + 2.8);
    const lines = pdf.splitTextToSize(c, contentW - 8) as string[];
    pdf.text(lines, MX + 6, y + 2.8);
    y += Math.max(4.6, lines.length * 3.3 + 1.4);
  });

  // ============================================================
  // §8 FOR OFFICE USE ONLY (sign-off — blank)
  // ============================================================
  section('8.  FOR OFFICE USE ONLY');

  /** Signature line: label above a full-width rule + a short date rule. */
  function signRow(label: string) {
    flushRows();
    ensure(12);
    setT(PDF.MUTED);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.text(label.toUpperCase(), MX + 1, y);
    const dateW = 42;
    const sigW = contentW - dateW - 6;
    const ly = y + 6.5;
    setD(PDF.RULE);
    pdf.setLineWidth(0.2);
    pdf.line(MX + 1, ly, MX + 1 + sigW, ly);
    pdf.line(right - dateW, ly, right, ly);
    setT(PDF.MUTED);
    pdf.setFontSize(6.4);
    pdf.text('Signature', MX + 1, ly + 3);
    pdf.text('Date', right - dateW, ly + 3);
    y += 12;
  }

  /** A comment area: label + N blank rules. */
  function commentRows(label: string, lines = 2) {
    flushRows();
    ensure(8 + lines * 5);
    setT(PDF.MUTED);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.text(label.toUpperCase(), MX + 1, y);
    y += 4;
    setD(PDF.RULE);
    pdf.setLineWidth(0.2);
    for (let i = 0; i < lines; i++) {
      y += 5;
      pdf.line(MX + 1, y, right, y);
    }
    y += 3;
  }

  subhead('Parent / guardian declaration');
  para(
    'I/We declare that the information given in this application is true and complete, and I/We ' +
      'have read and accept the Conditions of Admission set out in section 7.',
    7.6,
  );
  y += 1;
  signRow('Parent / guardian signature');

  subhead('Deputy Principal');
  commentRows('Comment', 2);
  signRow('Deputy Principal signature');

  subhead('Principal');
  commentRows('Comment', 2);
  signRow('Principal signature');

  /* PRINTED ALREADY ANSWERED where the assessment has been recorded.
     This box existed from the start and was always filled in by hand, then
     typed nowhere. Now the office records the marks in the portal and the
     printed form carries them — and where it has not been recorded yet, the
     blanks are still there to write on, because printing the form is how
     the test gets run. */
  subhead('Academics Office');
  {
    const ea = app.entranceAssessment;
    const pct = ea ? entranceOverallPercent(ea.marks ?? []) : null;
    row(
      ['Entrance exam date', ea?.sittingDate ?? ''],
      [
        'Passed (YES / NO)',
        ea
          ? `${ea.outcome === 'pass' ? 'YES' : ea.outcome === 'fail' ? 'NO' : 'EXEMPT'}`
            + (pct === null ? '' : `  (${pct}%)`)
          : '',
      ],
    );
  }

  subhead('Accounts Office');
  row(['Fee amount', ''], ['Receipt number', '']);
  row(['Date', ''], ['', '']);
  flushRows();
  ensure(26);
  setT(PDF.MUTED);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7);
  pdf.text('SCHOOL STAMP', right - 42, y + 2);
  setD(PDF.RULE);
  pdf.setLineWidth(0.3);
  pdf.rect(right - 42, y + 4, 42, 20);
  y += 26;

  flushRows();
  stampFooters(pdf, { geom, generatedBy: 'Admissions' });
  return pdf;
}
